"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { type VariantProps } from "class-variance-authority";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import { cn } from "../lib/cn";
import { toggleVariants } from "./toggle";
const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants> & {
  spacing?: number;
}>({
  size: "default",
  variant: "default",
  spacing: 0
});
function ToggleGroup(t0) {
  const $ = _c(26);
  let children;
  let className;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      spacing: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    variant = $[6];
  }
  const spacing = t1 === undefined ? 0 : t1;
  let t2;
  if ($[7] !== spacing) {
    t2 = {
      "--gap": spacing
    };
    $[7] = spacing;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = t2 as React.CSSProperties;
  let t4;
  if ($[9] !== className) {
    t4 = cn("group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs", className);
    $[9] = className;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== size || $[12] !== spacing || $[13] !== variant) {
    t5 = {
      variant,
      size,
      spacing
    };
    $[11] = size;
    $[12] = spacing;
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== children || $[16] !== t5) {
    t6 = <ToggleGroupContext.Provider value={t5}>{children}</ToggleGroupContext.Provider>;
    $[15] = children;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props || $[19] !== size || $[20] !== spacing || $[21] !== t3 || $[22] !== t4 || $[23] !== t6 || $[24] !== variant) {
    t7 = <ToggleGroupPrimitive.Root data-slot="toggle-group" data-variant={variant} data-size={size} data-spacing={spacing} style={t3} className={t4} {...props}>{t6}</ToggleGroupPrimitive.Root>;
    $[18] = props;
    $[19] = size;
    $[20] = spacing;
    $[21] = t3;
    $[22] = t4;
    $[23] = t6;
    $[24] = variant;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function ToggleGroupItem(t0) {
  const $ = _c(17);
  let children;
  let className;
  let props;
  let size;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      children,
      variant,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    variant = $[5];
  }
  const context = React.useContext(ToggleGroupContext);
  const resolvedVariant = context.variant ?? variant;
  const resolvedSize = context.size ?? size;
  const t1 = context.spacing;
  let t2;
  if ($[6] !== className || $[7] !== resolvedSize || $[8] !== resolvedVariant) {
    t2 = cn(toggleVariants({
      variant: resolvedVariant,
      size: resolvedSize
    }), "w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10", "data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-md data-[spacing=0]:last:rounded-r-md data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l", className);
    $[6] = className;
    $[7] = resolvedSize;
    $[8] = resolvedVariant;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== context.spacing || $[12] !== props || $[13] !== resolvedSize || $[14] !== resolvedVariant || $[15] !== t2) {
    t3 = <ToggleGroupPrimitive.Item data-slot="toggle-group-item" data-variant={resolvedVariant} data-size={resolvedSize} data-spacing={t1} className={t2} {...props}>{children}</ToggleGroupPrimitive.Item>;
    $[10] = children;
    $[11] = context.spacing;
    $[12] = props;
    $[13] = resolvedSize;
    $[14] = resolvedVariant;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
export { ToggleGroup, ToggleGroupItem };